import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { hubClient, erpClient, agwClient } from '@tfpl/api-client';
import { getConfig } from '@/config';
import { PermissionsMap } from '@/composables/auth/useAuthorization';
import { setPrimaryAbsenceApprover } from '@/utils/auth';

interface AgwUserPayload {
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string;
  uuid: string;
  permissions: agwClient.CompanyPermissionsDto[];
  mappedPermissions: Map<string, PermissionsMap>;
}

interface ExtendedUsersDto extends hubClient.CollaboratorsDto {
  certifications?: Array<{
    name: string;
    description: string;
  }>;
}

export const useAuthStore = defineStore('auth', () => {
  const currentUser = ref<ExtendedUsersDto | null>(null);
  const currentAgwUser = ref<AgwUserPayload | null>(null);
  const isLoading = ref<boolean>(true);
  const isImpersonated = ref<boolean>(false);
  const impersonatorUuid = ref<string | null>(null);

  const isLogged = computed(() => currentUser.value !== null);

  const setUser = (user: ExtendedUsersDto | null) => {
    currentUser.value = user;
  };

  const setAgwUser = (user: AgwUserPayload | null) => {
    currentAgwUser.value = user;
  };

  const fetchUser = async () => {
    const { data: hubResponse } = await hubClient.V3CollaboratorsService.getMe({
      path: { tenant: getConfig().TENANT, company: getConfig().TENANT },
      throwOnError: true,
    });

    const hubData = hubResponse.data;

    if (!hubData) {
      throw new Error('User not found in Hub');
    }

    let erpData: erpClient.CollaboratorProfileOutputDto | null = null;

    try {
      const { data: erpResponse } = await erpClient.CollaboratorProfilesService.getOne({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
          collaboratorUuid: hubData.uuid,
        },
        throwOnError: true,
      });
      erpData = erpResponse.data;
    } catch (e) {
      console.error('Failed to fetch ERP profile', e);
    }

    const user = hubData as ExtendedUsersDto;

    if (erpData?.collaborator?.certifications) {
      user.certifications = erpData.collaborator.certifications.map((c) => ({
        name: c.name,
        description: c.description,
      }));
    }

    if (user.primaryOfficeAbsenceApprover) {
      setPrimaryAbsenceApprover(user.primaryOfficeAbsenceApprover);
    }

    return user;
  };

  const waitForAuth = async () => {
    return new Promise<void>((resolve) => {
      const unwatch = watch(isLoading, (newVal) => {
        if (!newVal) {
          unwatch();
          resolve();
        }
      });
    });
  };

  const setLoading = (value: boolean) => {
    isLoading.value = value;
  };

  const setImpersonated = (value: boolean) => {
    isImpersonated.value = value;
  };

  const setImpersonatorUuid = (uuid: string | null) => {
    impersonatorUuid.value = uuid;
  };

  return {
    isLogged,
    isLoading,
    currentUser,
    currentAgwUser,
    isImpersonated,
    impersonatorUuid,

    setUser,
    setAgwUser,
    fetchUser,
    waitForAuth,
    setLoading,
    setImpersonated,
    setImpersonatorUuid,
  };
});
